import { SelectOption } from "@veneer/core";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import UsersManagementService from "../../services/userManagementApi/usersManagement.service";
import { CellFormulaValue, CellHyperlinkValue, Workbook } from "exceljs";
import { CategoriesFundEnum } from "../../models/enum/userManagementApi/CategoriesFundEnum";
import { CategoriesEnum } from "../../models/enum/userManagementApi/CategoriesEnum";
import { IManageUser } from "../../models/input/userApi/IManageUser";

export const useUsersManagementMassive = () => {
  type SelectValue = number | string | boolean;

  const [t] = useTranslation("global");
  const languageID: any = useSelector((state: RootState) => state.languageId);
  const [openModalCreateDone, setOpenModalCreateDone] = useState(false);
  const [openModalError, setOpenModalError] = useState(false);

  const [actionTypeSelected, setActionTypeSelected] = useState<SelectValue[]>(
    []
  );
  const [disabledDownloadTemplate, setDisabledDownloadTemplate] =
    useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingModal, setLoadingModal] = useState<boolean>(false);
  const [buttonSaveDisabled, setButtonSaveDisabled] = useState(true);
  const [jsonUsers, setJsonUsers] = useState<IManageUser[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [messageError, setErrorMessage] = useState<string>("");
  const [errorFilePicker, setErrorFilePicker] = useState(false);
  const [openModalCreate, setOpenModalCreate] = useState(false);
  const [openModalHelp, setOpenModalHelp] = useState(false);
  const [actionTypeIDFile, setActionTypeIDFile] = useState(0);

  const optionsTypeAction: SelectOption[] = [
    { value: 1, label: t("header.create") },
    { value: 2, label: t("header.update") },
  ];

  const enum actionsEnum {
    create = 1,
    update = 2,
  }

  const handleDownloadTemplate = () => {
    setLoading(true);
    UsersManagementService.getTempateUsersManagementMassive(
      Number(actionTypeSelected),
      Number(languageID.value)
    ).then((r: Blob | any) => {
      try {
        if (r?.statusCode === 401) {
          console.error("Error:", "Download failed");
        } else if (r instanceof Blob) {
          try {
            let fileName = "template_{action}_users_management_massive";

            switch (Number(actionTypeSelected)) {
              case 1:
                fileName = fileName.replace("{action}", "create");
                break;
              case 2:
                fileName = fileName.replace("{action}", "update");
                break;
              default:
                fileName = fileName.replace("{action}", "");
            }

            const url = window.URL.createObjectURL(r);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName + ".xlsx");
            document.body.appendChild(link);
            link.click();
            window.URL.revokeObjectURL(url);
          } catch (error) {
            console.error("Error:", error);
          }
        }
      } catch (error) {
        console.error("Error:", error);
      } finally {
        setLoading(false);
        setActionTypeSelected([]);
      }
    });
  };

  useEffect(() => {
    if (actionTypeSelected.length > 0) {
      setDisabledDownloadTemplate(false);
    } else {
      setDisabledDownloadTemplate(true);
    }
  }, [actionTypeSelected]);

  const handleCreateUsers = () => {
    setActionTypeIDFile(0);
    setOpenModalCreate(true);
    setFileName("");
    setErrorFilePicker(false);
    setButtonSaveDisabled(true);
  };

  const handleOnClickExit = () => {
    setFileName("");
    setOpenModalCreate(false);
    setButtonSaveDisabled(true);
  };

  const categorySelectedApplyForPrintFund = (
    categorySelected: number
  ): boolean => {
    return (
      categorySelected === CategoriesEnum.Supplies ||
      categorySelected === CategoriesEnum.Printing ||
      categorySelected === CategoriesEnum.DYP ||
      categorySelected === CategoriesEnum.PPS
    );
  };

  const categorySelectedApplyForPCFund = (
    categorySelected: number
  ): boolean => {
    return (
      categorySelected === CategoriesEnum.Computing ||
      categorySelected === CategoriesEnum.PPS
    );
  };

  const getCategoryFundID = (isPrint: boolean, isPC: boolean): number => {
    if (isPrint && isPC) {
      return CategoriesFundEnum.Both;
    } else if (isPrint) {
      return CategoriesFundEnum.Print;
    } else if (isPC) {
      return CategoriesFundEnum.PC;
    } else {
      return 0;
    }
  };

  const generateErrorExcel = async (errorRows: any[]) => {
    console.log("generateErrorExcel");

    const workbook = new Workbook();
    const sheet = workbook.addWorksheet("Errores");

    // Headers
    sheet.addRow([
      "Fila",
      "Errores",
      "Category",
      "Partner Print",
      "Fund Print",
      "Partner PC",
      "Fund PC",
    ]);

    // Estilo header
    sheet.getRow(1).font = { bold: true };

    // Data
    errorRows.forEach((err) => {
      sheet.addRow([
        err.row,
        err.errors,
        err.data?.category || "",
        err.data?.partnerPrint || "",
        err.data?.fundPrint || "",
        err.data?.partnerPC || "",
        err.data?.fundPC || "",
      ]);
    });

    // Auto width
    sheet.columns.forEach((col) => {
      col.width = 25;
    });

    // Descargar archivo
    const buffer = await workbook.xlsx.writeBuffer();

    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "Errores_Carga_Usuarios.xlsx";
    link.click();
  };

  const handleMexicoFile = async (file: File) => {
    try {
      setFileName(file.name);

      const reader = new FileReader();
      const fileType = file.name.split(".").pop();
      if (fileType?.toLowerCase() !== "xlsx") {
        setErrorMessage(t("header.messageXLSX"));
        setErrorFilePicker(true);
        return;
      }

      setLoadingModal(true);
      setErrorFilePicker(false);

      reader.onload = async (event) => {
        const data = event.target?.result as ArrayBuffer;
        const workbook = new Workbook();

        try {
          await workbook.xlsx.load(data);
          const hiddenSheet = workbook.getWorksheet("Data");
          const advisorsSheet = workbook.getWorksheet("Advisors");

          if (!hiddenSheet || !advisorsSheet) {
            console.log("Error !hiddenSheet || !advisorsSheet");

            setErrorMessage(t("modal.errorLoadingFile"));
            setErrorFilePicker(true);
            setLoadingModal(false);
            return;
          }

          // Mapas de datos ocultos
          const countriesMap: Record<string, number> = {};
          const citiesMap: Record<string, number> = {};
          const timesMap: Record<string, number> = {};
          const categoriesMap: Record<string, number> = {};
          const partnersMap: Record<string, number> = {};
          const fundsMap: Record<string, number> = {};
          const fundsHPMap: Record<string, number> = {};
          const supervisorsMap: Record<string, number> = {};
          const rolesMap: Record<string, number> = {};
          const advisorsMap: Record<string, number> = {};

          // Leer hidden sheet
          let row = 1;

          // Countries
          while (
            hiddenSheet.getCell(`A${row}`).value &&
            hiddenSheet.getCell(`B${row}`).value
          ) {
            const id = Number(hiddenSheet.getCell(`A${row}`).value);
            const name = hiddenSheet
              .getCell(`B${row}`)
              .value?.toString()
              .trim();
            if (name) countriesMap[name] = id;
            row++;
          }

          // Cities
          row = 1;
          while (
            hiddenSheet.getCell(`E${row}`).value &&
            hiddenSheet.getCell(`F${row}`).value
          ) {
            const id = Number(hiddenSheet.getCell(`E${row}`).value);
            const name = hiddenSheet
              .getCell(`F${row}`)
              .value?.toString()
              .trim();
            if (name) citiesMap[name] = id;
            row++;
          }

          // Times
          row = 1;
          while (
            hiddenSheet.getCell(`G${row}`).value &&
            hiddenSheet.getCell(`H${row}`).value
          ) {
            const id = Number(hiddenSheet.getCell(`G${row}`).value);
            const name = hiddenSheet
              .getCell(`H${row}`)
              .value?.toString()
              .trim();
            if (name) timesMap[name] = id;
            row++;
          }

          // Categories
          row = 1;
          while (
            hiddenSheet.getCell(`I${row}`).value &&
            hiddenSheet.getCell(`J${row}`).value
          ) {
            const id = Number(hiddenSheet.getCell(`I${row}`).value);
            const name = hiddenSheet
              .getCell(`J${row}`)
              .value?.toString()
              .trim();
            if (name) categoriesMap[name] = id;
            row++;
          }

          // Partners
          row = 1;
          while (
            hiddenSheet.getCell(`K${row}`).value &&
            hiddenSheet.getCell(`L${row}`).value
          ) {
            const id = Number(hiddenSheet.getCell(`K${row}`).value);
            const name = hiddenSheet
              .getCell(`L${row}`)
              .value?.toString()
              .trim();
            if (name) partnersMap[name] = id;
            row++;
          }

          // Funds
          row = 1;
          while (
            hiddenSheet.getCell(`M${row}`).value &&
            hiddenSheet.getCell(`N${row}`).value
          ) {
            const id = Number(hiddenSheet.getCell(`M${row}`).value);
            const name = hiddenSheet
              .getCell(`N${row}`)
              .value?.toString()
              .trim();
            if (name) fundsMap[name] = id;
            row++;
          }

          // Funds HP
          row = 1;
          while (
            hiddenSheet.getCell(`O${row}`).value &&
            hiddenSheet.getCell(`P${row}`).value
          ) {
            const id = Number(hiddenSheet.getCell(`O${row}`).value);
            const name = hiddenSheet
              .getCell(`P${row}`)
              .value?.toString()
              .trim();
            if (name) fundsHPMap[name] = id;
            row++;
          }

          // Supervisors
          row = 1;
          while (
            hiddenSheet.getCell(`Q${row}`).value &&
            hiddenSheet.getCell(`R${row}`).value
          ) {
            const id = Number(hiddenSheet.getCell(`Q${row}`).value);
            const name = hiddenSheet
              .getCell(`R${row}`)
              .value?.toString()
              .trim();
            if (name) supervisorsMap[name] = id;
            row++;
          }

          // Roles
          row = 1;
          while (
            hiddenSheet.getCell(`S${row}`).value &&
            hiddenSheet.getCell(`T${row}`).value
          ) {
            const id = Number(hiddenSheet.getCell(`S${row}`).value);
            const name = hiddenSheet
              .getCell(`T${row}`)
              .value?.toString()
              .trim();
            if (name) rolesMap[name] = id;
            row++;
          }

          // Advisors
          row = 1;
          while (
            advisorsSheet.getCell(`A${row}`).value &&
            advisorsSheet.getCell(`B${row}`).value
          ) {
            const id = Number(advisorsSheet.getCell(`A${row}`).value);
            const name = advisorsSheet
              .getCell(`B${row}`)
              .value?.toString()
              .trim();
            if (name) advisorsMap[name] = id;
            row++;
          }

          // Leer filas del Excel de México
          const rows: IManageUser[] = [];
          const errorRows: any[] = [];

          advisorsSheet.eachRow((row, rowNumber) => {
            if (rowNumber === 1) return;

            const advisorName = getValueCellTemplate(row.getCell(1).value);
            const lastName = getValueCellTemplate(row.getCell(2).value);
            const document = getValueCellTemplate(row.getCell(3).value);
            const country = getValueCellTemplate(row.getCell(4).value);
            const city = getValueCellTemplate(row.getCell(6).value);
            const phone = getValueCellTemplate(row.getCell(7).value);
            const email = getValueCellTemplate(row.getCell(8).value);
            const supervisor = getValueCellTemplate(row.getCell(9).value);
            const role = getValueCellTemplate(row.getCell(10).value);
            const time = getValueCellTemplate(row.getCell(11).value);
            const category = getValueCellTemplate(row.getCell(12).value);
            const partnerPrint = getValueCellTemplate(row.getCell(13).value);
            const fundPrint = getValueCellTemplate(row.getCell(14).value);
            const fundHPPrint = getValueCellTemplate(row.getCell(15).value);
            const partnerPC = getValueCellTemplate(row.getCell(16).value);
            const fundPC = getValueCellTemplate(row.getCell(17).value);
            const fundHPPC = getValueCellTemplate(row.getCell(18).value);
            const feePC =
              getValueCellTemplate(row.getCell(19).value) === t("header.yes")
                ? 1
                : 0;
            const feePrint =
              getValueCellTemplate(row.getCell(20).value) === t("header.yes")
                ? 1
                : 0;

            const categoryID = categoriesMap[category || ""] ?? 0;
            const requirePrintFund =
              categorySelectedApplyForPrintFund(categoryID);
            const requirePCFund = categorySelectedApplyForPCFund(categoryID);

            // Validación: columnas requeridas según categoría
            let hasError = false;
            const missingColumns: string[] = [];
            if (requirePrintFund) {
              if (!partnerPrint || !partnersMap[partnerPrint])
                missingColumns.push("Partner Print");
              if (!fundPrint || !fundsMap[fundPrint])
                missingColumns.push("Fund Print");
            }
            if (requirePCFund) {
              if (!partnerPC || !partnersMap[partnerPC])
                missingColumns.push("Partner PC");
              if (!fundPC || !fundsMap[fundPC]) missingColumns.push("Fund PC");
            }

            if (missingColumns.length > 0) {
              hasError = true;
              errorRows.push({
                rowNumber,
                columns: missingColumns.join(", "),
                message: "Value does not exist in hidden data",
              });
            }

            // Si no hay error, agrega fila al array final
            if (!hasError) {
              rows.push({
                UserID: advisorName ? (advisorsMap[advisorName] ?? 0) : 0,
                Name: advisorName ?? "",
                LastName: lastName ?? "",
                NumberID: document ?? "",
                CountryID: countriesMap[country || ""] ?? 0,
                CityID: citiesMap[city || 0] ?? 0,
                Phone: phone ? phone.replace(/\D/g, "") : "",
                Email: email ?? "",
                SupervisorID: supervisorsMap[supervisor || 0] ?? 0,
                RoleID: rolesMap[role || ""] ?? 0,
                TimeID: timesMap[time || ""] ?? 0,
                CategoryID: categoryID,
                PartnerPrintID:
                  requirePrintFund && partnerPrint
                    ? (partnersMap[partnerPrint] ?? 0)
                    : 0,
                FundPrintID:
                  requirePrintFund && fundPrint
                    ? (fundsMap[fundPrint] ?? 0)
                    : 0,
                FundHpPrintID:
                  requirePrintFund && fundHPPrint
                    ? (fundsHPMap[fundHPPrint] ?? 0)
                    : 0,
                PartnerPCID:
                  requirePCFund && partnerPC
                    ? (partnersMap[partnerPC] ?? 0)
                    : 0,
                FundPCID: requirePCFund && fundPC ? (fundsMap[fundPC] ?? 0) : 0,
                FundHpPCID:
                  requirePCFund && fundHPPC ? (fundsHPMap[fundHPPC] ?? 0) : 0,
                CategoryFundID:
                  getCategoryFundID(requirePrintFund, requirePCFund) ?? 0,
                FeePC: feePC,
                FeePrint: feePrint,
              });
            }
          });

          // Guardar JSON y generar Excel de errores si hay
          setJsonUsers(rows);

          if (errorRows.length > 0) {
            generateErrorExcel(errorRows);
          }
        } catch (err) {
          console.log("Error catch");

          setErrorMessage(t("modal.errorLoadingFile"));
          setErrorFilePicker(true);
        } finally {
          setLoadingModal(false);
        }
      };

      reader.readAsArrayBuffer(file);
      setButtonSaveDisabled(false);
    } catch (err) {
      console.log("Error general handleMexicoFile", err);
      setErrorMessage(t("modal.errorLoadingFile"));
      setErrorFilePicker(true);
      setLoadingModal(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("e.target:", e.target);
    console.log("files:", (e.target as HTMLInputElement).files);
    const fileInput = e.target;
    if (!fileInput.files) {
      console.log("Error handleFileChange !fileInput.files");

      setErrorMessage(t("modal.errorLoadingFile"));
      setErrorFilePicker(true);
      return;
    }

    const file = fileInput.files[0];
    setFileName(file.name);
    const reader = new FileReader();
    const fileType = file.name.split(".").pop();

    if (fileType?.toLowerCase() !== "xlsx") {
      setErrorMessage(t("header.messageXLSX"));
      setErrorFilePicker(true);
      return;
    }

    setLoadingModal(true);
    setErrorFilePicker(false);
    reader.onload = async (event) => {
      const data = event.target?.result as ArrayBuffer;
      const workbook = new Workbook();

      try {
        await workbook.xlsx.load(data);
        const hiddenSheet = workbook.getWorksheet("Data");
        const advisorsSheet = workbook.getWorksheet("Advisors");
        var actionTypeID = 0; // 1 for create and 2 for update
        var countriesMap: Record<string, number> = {};
        var citiesMap: Record<string, number> = {};
        var timesMap: Record<string, number> = {};
        var categoriesMap: Record<string, number> = {};
        var partnersMap: Record<string, number> = {};
        var fundsMap: Record<string, number> = {};
        var fundsHPMap: Record<string, number> = {};
        var supervisorsMap: Record<string, number> = {};
        var rolesMap: Record<string, number> = {};
        var advisorsMap: Record<string, number> = {};
        const rows: any[] = [];

        if (hiddenSheet) {
          actionTypeID = Number(hiddenSheet.getCell("U1").value);
          const countryID = Number(hiddenSheet.getCell("V1").value ?? 0);

          setActionTypeIDFile(actionTypeID);

          if (countryID === 12) {
            await handleMexicoFile(file);
            return;
          }

          // set map of data list
          // countries list
          let row = 1;
          while (
            hiddenSheet.getCell(`A${row}`).value &&
            hiddenSheet.getCell(`B${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`A${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`B${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !countriesMap[propertyName?.trim()]) {
              countriesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // cities list
          row = 1;
          while (
            hiddenSheet.getCell(`E${row}`).value &&
            hiddenSheet.getCell(`F${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`E${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`F${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !citiesMap[propertyName?.trim()]) {
              citiesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // times list
          row = 1;
          while (
            hiddenSheet.getCell(`G${row}`).value &&
            hiddenSheet.getCell(`H${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`G${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`H${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !timesMap[propertyName?.trim()]) {
              timesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // categories list
          row = 1;
          while (
            hiddenSheet.getCell(`I${row}`).value &&
            hiddenSheet.getCell(`J${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`I${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`J${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !categoriesMap[propertyName?.trim()]) {
              categoriesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // categories list
          row = 1;
          while (
            hiddenSheet.getCell(`I${row}`).value &&
            hiddenSheet.getCell(`J${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`I${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`J${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !categoriesMap[propertyName?.trim()]) {
              categoriesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // partners list
          row = 1;
          while (
            hiddenSheet.getCell(`K${row}`).value &&
            hiddenSheet.getCell(`L${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`K${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`L${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !partnersMap[propertyName?.trim()]) {
              partnersMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // funds list
          row = 1;
          while (
            hiddenSheet.getCell(`M${row}`).value &&
            hiddenSheet.getCell(`N${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`M${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`N${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !fundsMap[propertyName?.trim()]) {
              fundsMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // funds HP list
          row = 1;
          while (
            hiddenSheet.getCell(`O${row}`).value &&
            hiddenSheet.getCell(`P${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`O${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`P${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !fundsHPMap[propertyName?.trim()]) {
              fundsHPMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // supervisors list
          row = 1;
          while (
            hiddenSheet.getCell(`Q${row}`).value &&
            hiddenSheet.getCell(`R${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`Q${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`R${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !supervisorsMap[propertyName?.trim()]) {
              supervisorsMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }

          // roles list
          row = 1;
          while (
            hiddenSheet.getCell(`S${row}`).value &&
            hiddenSheet.getCell(`T${row}`).value
          ) {
            const propertyID = Number(hiddenSheet.getCell(`S${row}`).value);
            const propertyName = hiddenSheet
              .getCell(`T${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !rolesMap[propertyName?.trim()]) {
              rolesMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }
        }

        if (advisorsSheet && actionTypeID === actionsEnum.update) {
          let row = 1;
          // set map of data list
          while (
            advisorsSheet.getCell(`A${row}`).value &&
            advisorsSheet.getCell(`B${row}`).value
          ) {
            const propertyID = Number(advisorsSheet.getCell(`A${row}`).value);
            const propertyName = advisorsSheet
              .getCell(`B${row}`)
              .value?.toString()
              .trim();

            if (propertyName?.trim() && !advisorsMap[propertyName?.trim()]) {
              advisorsMap[propertyName?.trim()] = propertyID;
            }
            row++;
          }
        }

        if (!actionTypeID) {
          setErrorMessage(t("header.messageFormatError"));
          setErrorFilePicker(true);
          return;
        }

        const sheet = workbook.worksheets[0];
        sheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) {
            return;
          }

          let advisorName,
            name,
            lastName,
            document,
            country,
            city,
            phone,
            email,
            supervisor,
            role,
            time,
            category,
            partnerPrint,
            fundPrint,
            fundHPPrint,
            partnerPC,
            fundPC,
            fundHPPC,
            feePC,
            feePrint;

          let yesValue = t("header.yes");
          let feePCLabel = getValueCellTemplate(row.getCell(19).value);
          let feePCValue = feePCLabel === yesValue ? 1 : 0;

          let feePrintLabel = getValueCellTemplate(row.getCell(20).value);
          let feePrintValue = feePrintLabel === yesValue ? 1 : 0;

          if (actionTypeID === actionsEnum.create) {
            name = getValueCellTemplate(row.getCell(1).value);
            lastName = getValueCellTemplate(row.getCell(2).value);
            document = getValueCellTemplate(row.getCell(3).value);
            country = getValueCellTemplate(row.getCell(4).value);
            city = getValueCellTemplate(row.getCell(6).value);
            phone = getValueCellTemplate(row.getCell(7).value);
            email = getValueCellTemplate(row.getCell(8).value);
            supervisor = getValueCellTemplate(row.getCell(9).value);
            role = getValueCellTemplate(row.getCell(10).value);
            time = getValueCellTemplate(row.getCell(11).value);
            category = getValueCellTemplate(row.getCell(12).value);
            partnerPrint = getValueCellTemplate(row.getCell(13).value);
            fundPrint = getValueCellTemplate(row.getCell(14).value);
            fundHPPrint = getValueCellTemplate(row.getCell(15).value);
            partnerPC = getValueCellTemplate(row.getCell(16).value);
            fundPC = getValueCellTemplate(row.getCell(17).value);
            fundHPPC = getValueCellTemplate(row.getCell(18).value);
            feePC = feePCValue;
            feePrint = feePrintValue;
          }

          if (
            actionTypeID === actionsEnum.update &&
            !getValueCellTemplate(row.getCell(1).value)
          ) {
            return;
          }

          if (actionTypeID === actionsEnum.update) {
            yesValue = t("header.yes");
            feePCLabel = getValueCellTemplate(row.getCell(18).value);
            feePCValue = feePCLabel === yesValue ? 1 : 0;

            feePrintLabel = getValueCellTemplate(row.getCell(19).value);
            feePrintValue = feePrintLabel === yesValue ? 1 : 0;

            advisorName = getValueCellTemplate(row.getCell(1).value);
            document = getValueCellTemplate(row.getCell(2).value);
            country = getValueCellTemplate(row.getCell(3).value);
            city = getValueCellTemplate(row.getCell(5).value);
            phone = getValueCellTemplate(row.getCell(6).value);
            email = getValueCellTemplate(row.getCell(7).value);
            supervisor = getValueCellTemplate(row.getCell(8).value);
            role = getValueCellTemplate(row.getCell(9).value);
            time = getValueCellTemplate(row.getCell(10).value);
            category = getValueCellTemplate(row.getCell(11).value);
            partnerPrint = getValueCellTemplate(row.getCell(12).value);
            fundPrint = getValueCellTemplate(row.getCell(13).value);
            fundHPPrint = getValueCellTemplate(row.getCell(14).value);
            partnerPC = getValueCellTemplate(row.getCell(15).value);
            fundPC = getValueCellTemplate(row.getCell(16).value);
            fundHPPC = getValueCellTemplate(row.getCell(17).value);
            feePC = feePCValue;
            feePrint = feePrintValue;
          }

          const requirePrintFund = categorySelectedApplyForPrintFund(
            Number(categoriesMap[category || 0])
          );
          const requirePCFund = categorySelectedApplyForPCFund(
            Number(categoriesMap[category || 0])
          );

          if (
            ((actionTypeID === actionsEnum.update &&
              advisorName &&
              advisorsMap[advisorName.trim()]) ||
              actionTypeID === actionsEnum.create) &&
            ((actionTypeID === actionsEnum.create && name && lastName) ||
              actionTypeID === actionsEnum.update) &&
            document &&
            phone &&
            email &&
            country &&
            countriesMap[country] &&
            city &&
            citiesMap[city] &&
            supervisor &&
            supervisorsMap[supervisor] &&
            role &&
            rolesMap[role] &&
            time &&
            timesMap[time] &&
            category &&
            categoriesMap[category] &&
            ((requirePrintFund &&
              partnerPrint &&
              partnersMap[partnerPrint] &&
              fundPrint &&
              fundsMap[fundPrint]) ||
              !requirePrintFund) &&
            ((requirePCFund &&
              partnerPC &&
              partnersMap[partnerPC] &&
              fundPC &&
              fundsMap[fundPC]) ||
              !requirePCFund)
          ) {
            const categoryFundID = getCategoryFundID(
              requirePrintFund,
              requirePCFund
            );

            const rowData: IManageUser = {
              UserID: 0,
              Name: name,
              LastName: lastName,
              NumberID: document,
              CountryID: countriesMap[country],
              CityID: citiesMap[city],
              Phone: phone.replace(/\D/g, ""),
              Email: email,
              SupervisorID: supervisorsMap[supervisor],
              RoleID: rolesMap[role],
              TimeID: timesMap[time],
              CategoryID: categoriesMap[category],
              PartnerPrintID:
                partnerPrint && requirePrintFund
                  ? partnersMap[partnerPrint]
                  : 0,
              FundPrintID:
                fundPrint && requirePrintFund ? fundsMap[fundPrint] : 0,
              FundHpPrintID:
                fundHPPrint && requirePrintFund ? fundsHPMap[fundHPPrint] : 0,
              PartnerPCID:
                partnerPC && requirePCFund ? partnersMap[partnerPC] : 0,
              FundPCID: fundPC && requirePCFund ? fundsMap[fundPC] : 0,
              FundHpPCID: fundHPPC && requirePCFund ? fundsHPMap[fundHPPC] : 0,
              CategoryFundID: categoryFundID,
              FeePC: feePC ? Number(feePC) : 0,
              FeePrint: feePrint ? Number(feePrint) : 0,
            };

            if (actionTypeID === actionsEnum.update) {
              rowData.UserID = advisorName ? advisorsMap[advisorName] : 0;
            }

            rows.push(rowData);
          } else {
            setButtonSaveDisabled(true);
            setErrorFilePicker(true);
            setErrorMessage(
              t("modal.invalidDataUsersMassive").replace(
                "{0}",
                rowNumber.toString()
              )
            );
          }
        });
        setJsonUsers(rows);
      } catch (error) {
        console.log("Error catch handleFileChange");

        setErrorFilePicker(true);
        setButtonSaveDisabled(true);
        setErrorMessage(t("modal.errorLoadingFile"));
      }

      setLoadingModal(false);
    };

    reader.readAsArrayBuffer(file);
    setButtonSaveDisabled(false);
  };

  const onClearFilePicker = () => {
    setFileName("");
    setErrorFilePicker(false);
    setButtonSaveDisabled(true);
  };

  const submitUsers = async () => {
    setLoadingModal(true);
    interface DataItem {
      userID: number;
      email?: string;
      numberID?: string;
      actionTypeID: number;
      result: number;
      message?: string;
    }

    try {
      const response: any[] = await UsersManagementService.manageUsersInBulk(
        jsonUsers,
        actionTypeIDFile
      );

      if (!response) {
        setOpenModalCreate(false);
        setOpenModalError(true);
        return;
      }

      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Datos");

      worksheet.columns = [
        { header: t("header.userId"), key: "userID", width: 15 },
        { header: t("header.email"), key: "email", width: 15 },
        { header: t("header.document"), key: "numberID", width: 15 },
        { header: t("modal.message"), key: "message", width: 30 },
      ];

      response.forEach((item: DataItem) => {
        if (item.actionTypeID === actionsEnum.create) {
          item.message =
            item.result === 1
              ? t("modal.userCreated")
              : t("modal.userNotCreated");
        }

        if (item.actionTypeID === actionsEnum.update) {
          item.message =
            item.result === 1
              ? t("modal.updateInfo")
              : t("modal.informationNotUpdated");
        }

        const row = worksheet.addRow(item);

        row.eachCell((cell) => {
          cell.alignment = { horizontal: "center" };
        });
      });

      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "00B0F0" },
        };
        cell.alignment = { horizontal: "center" };
        cell.border = {
          top: { style: "thin", color: { argb: "000000" } },
          left: { style: "thin", color: { argb: "000000" } },
          bottom: { style: "thin", color: { argb: "000000" } },
          right: { style: "thin", color: { argb: "000000" } },
        };
        cell.font = { bold: true };
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const url = URL.createObjectURL(blob);

      setOpenModalCreate(false);
      setOpenModalCreateDone(true);

      const a = document.createElement("a");
      a.href = url;
      a.download = "users_result.xlsx";
      a.click();

      URL.revokeObjectURL(url);
      setLoadingModal(false);
    } catch (error) {
      setOpenModalCreate(false);
      setOpenModalError(true);
      setLoadingModal(false);
    }
  };

  const getValueCellTemplate = (cellValue: any): string | undefined => {
    if (typeof cellValue === "string" || typeof cellValue === "number") {
      return cellValue?.toString().trim();
    }

    if ((cellValue as CellHyperlinkValue)?.text) {
      return (cellValue as CellHyperlinkValue)?.text?.toString().trim();
    }

    if ((cellValue as CellFormulaValue)?.result) {
      return (cellValue as CellFormulaValue)?.result?.toString().trim();
    }

    return;
  };

  return {
    optionsTypeAction,
    actionTypeSelected,
    setActionTypeSelected,
    handleCreateUsers,
    setOpenModalHelp,
    openModalHelp,
    loading,
    disabledDownloadTemplate,
    handleDownloadTemplate,
    loadingModal,
    openModalCreate,
    handleOnClickExit,
    buttonSaveDisabled,
    handleFileChange,
    fileName,
    errorFilePicker,
    messageError,
    submitUsers,
    onClearFilePicker,
    openModalCreateDone,
    openModalError,
    setOpenModalCreateDone,
    setOpenModalError,
  };
};
